import { Bar, CalendarMode, Row } from "PFApp/booking/types";
import { ActivityWarningsIndex } from "PFCore/hooks/queries/activity";
import { ProfileBookingsIndex, ProfileWarningsIndex } from "PFCore/hooks/queries/profile";
import { ProfileAvailabilitiesIndex } from "PFCore/hooks/queries/profile/use_profiles_availabilities";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";

import { useBookingsOverviewContext } from "../bookings_overview_context";
import { useProjectsRows } from "./use_projects_rows";
import { useRowsAdditionalData } from "./use_rows_additional_data";
import { useWorkforceRows } from "./use_workforce_rows";

const ALL_BOOKINGS_MODE_INITIAL_STATE = {};
const EXPANDED_ROWS_INITIAL_STATE = {
  [CalendarMode.Projects]: {},
  [CalendarMode.Workforce]: {}
};
const ROWS_INITIAL_STATE = {
  [CalendarMode.Projects]: [],
  [CalendarMode.Workforce]: []
};
const ROW_DIMENSIONS_INITIAL_STATE = {
  [CalendarMode.Projects]: {},
  [CalendarMode.Workforce]: {}
};

export type AllBookingsModeRowsStateType = Record<string, boolean | undefined>;
type ExpandedRowsStateType = Record<CalendarMode, Record<string, boolean | undefined>>;
type RowsByCalendarModeStateType = Record<CalendarMode, Row[]>;
type RowDimensionsStateType = Record<CalendarMode, RowDimensions>;
export type RowDimensions = Record<string, { top: number; height: number }>;

export type BookingRowsContextType = {
  rowsByCalendarMode: RowsByCalendarModeStateType;
  setRowsByCalendarMode: Dispatch<SetStateAction<RowsByCalendarModeStateType>>;
  rowDimensionsByCalendarMode: RowDimensionsStateType;
  setRowDimensionsByCalendarMode: Dispatch<SetStateAction<RowDimensionsStateType>>;
  allBookingsModeRowsState: AllBookingsModeRowsStateType;
  setAllBookingsModeRowsState: React.Dispatch<SetStateAction<AllBookingsModeRowsStateType>>;
  expandedRowsState: ExpandedRowsStateType;
  setExpandedRowsState: React.Dispatch<SetStateAction<ExpandedRowsStateType>>;
  resetExpandedRowsState: VoidFunction;
  bookingsByRowKey: ProfileBookingsIndex;
  barsByRowKey: Record<string, Bar[][]>;
  rowsByKey: Record<string, Row>;
  warningsByActivity: ActivityWarningsIndex;
  warningsByProfile: ProfileWarningsIndex;
  availabilitiesByProfile: ProfileAvailabilitiesIndex;
  rowsLoadingByKey: Record<string, boolean>;
};

const BookingRowsContext = createContext<BookingRowsContextType>({} as BookingRowsContextType);

export const BookingRowsContextProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const { calendarMode } = useBookingsOverviewContext();

  const [rowsByCalendarMode, setRowsByCalendarMode] =
    useState<RowsByCalendarModeStateType>(ROWS_INITIAL_STATE);
  const [rowDimensionsByCalendarMode, setRowDimensionsByCalendarMode] = useState<RowDimensionsStateType>(
    ROW_DIMENSIONS_INITIAL_STATE
  );
  const [allBookingsModeRowsState, setAllBookingsModeRowsState] = useState<AllBookingsModeRowsStateType>(
    ALL_BOOKINGS_MODE_INITIAL_STATE
  );
  const [expandedRowsState, setExpandedRowsState] =
    useState<ExpandedRowsStateType>(EXPANDED_ROWS_INITIAL_STATE);

  const resetExpandedRowsState = useCallback(
    () =>
      setExpandedRowsState((prevState) => ({
        ...prevState,
        [calendarMode]: {}
      })),
    [calendarMode]
  );
  const resetAllBookingsModeState = useCallback(
    () => setAllBookingsModeRowsState(ALL_BOOKINGS_MODE_INITIAL_STATE),
    []
  );

  useProjectsRows({
    setRowsByCalendarMode,
    onRowsReplace: () => {
      resetExpandedRowsState();
      resetAllBookingsModeState();
    }
  });

  useWorkforceRows({
    setRowsByCalendarMode,
    onRowsReplace: () => {
      resetExpandedRowsState();
      resetAllBookingsModeState();
    }
  });

  const rowsAdditionalData = useRowsAdditionalData({
    rowsByCalendarMode,
    allBookingsModeRowsState
  });

  const contextValue = useMemo<BookingRowsContextType>(
    () => ({
      rowsByCalendarMode,
      setRowsByCalendarMode,
      rowDimensionsByCalendarMode,
      setRowDimensionsByCalendarMode,
      allBookingsModeRowsState,
      setAllBookingsModeRowsState,
      expandedRowsState,
      setExpandedRowsState,
      resetExpandedRowsState,
      ...rowsAdditionalData
    }),
    [
      rowsByCalendarMode,
      rowDimensionsByCalendarMode,
      allBookingsModeRowsState,
      expandedRowsState,
      resetExpandedRowsState,
      JSON.stringify(rowsAdditionalData)
    ]
  );

  return <BookingRowsContext.Provider value={contextValue}>{children}</BookingRowsContext.Provider>;
};

export const useBookingRowsContext = () =>
  useContext<BookingRowsContextType>(BookingRowsContext) as BookingRowsContextType;
